export interface InstrumentOption {
  symbol: string;
  name: string;
  group: string;
  beta: number;
  fallbackPrice: number;
}

export const INSTRUMENT_GROUPS: Array<{
  label: string;
  instruments: InstrumentOption[];
}> = [
  {
    label: "Equity Index Futures",
    instruments: [
      option("/NQ", "Nasdaq 100", 1.2, 21000),
      option("/ES", "S&P 500", 1, 6000),
      option("/YM", "Dow Jones", 0.95, 44000),
      option("/RTY", "Russell 2000", 1.1, 2200),
      option("/MNQ", "Micro Nasdaq", 1.2, 21000),
      option("/MES", "Micro S&P", 1, 6000),
      option("/MYM", "Micro Dow", 0.95, 44000),
      option("/M2K", "Micro Russell", 1.1, 2200),
    ],
  },
  {
    label: "Commodities",
    instruments: [
      option("/CL", "Crude Oil", 0.6, 75),
      option("/GC", "Gold", 0.2, 2650),
      option("/SI", "Silver", 0.4, 30),
      option("/NG", "Natural Gas", 0.5, 3.5),
      option("/MCL", "Micro Crude", 0.6, 75),
      option("/MGC", "Micro Gold", 0.2, 2650),
      option("/SIL", "Micro Silver", 0.4, 30),
    ],
  },
  {
    label: "Bonds",
    instruments: [
      option("/ZB", "30-Year T-Bond", -0.3, 118),
      option("/ZN", "10-Year T-Note", -0.25, 110),
      option("/ZT", "2-Year T-Note", -0.2, 108),
    ],
  },
  {
    label: "Crypto",
    instruments: [
      option("/BTC", "Bitcoin Futures", 0.15, 95000),
      option("/ETH", "Ethereum Futures", 0.18, 3500),
    ],
  },
  {
    label: "Currencies",
    instruments: [
      option("/6E", "Euro FX", 0.3, 1.08),
      option("/6J", "Japanese Yen", 0.25, 0.0067),
      option("/6B", "British Pound", 0.35, 1.27),
      option("/6A", "Australian Dollar", 0.2, 0.67),
      option("/6C", "Canadian Dollar", 0.25, 0.74),
      option("/6S", "Swiss Franc", 0.15, 1.1),
    ],
  },
].map((group) => ({
  ...group,
  instruments: group.instruments.map((instrument) => ({
    ...instrument,
    group: group.label,
  })),
}));

export const SUPPORTED_INSTRUMENTS = INSTRUMENT_GROUPS.flatMap(
  (group) => group.instruments,
);

const INSTRUMENT_BY_SYMBOL = new Map(
  SUPPORTED_INSTRUMENTS.map((instrument) => [instrument.symbol, instrument]),
);

const SEVERITY_FRACTION: Record<string, number> = {
  critical: 0.8,
  high: 0.5,
  medium: 0.3,
  low: 0.15,
};

export function normalizeInstrumentSymbol(
  value: string | null | undefined,
  fallback = "/NQ",
): string {
  const raw = value?.trim().toUpperCase();
  if (!raw) return fallback;
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  return INSTRUMENT_BY_SYMBOL.has(withSlash) ? withSlash : fallback;
}

export function activeInstrumentFromSettings(input: {
  selectedInstrument?: string | null;
  selectedSymbol?: { symbol?: string | null } | string | null;
  fallback?: string;
}): string {
  const fallback = input.fallback ?? "/NQ";
  const preferred = normalizeInstrumentSymbol(
    input.selectedInstrument,
    fallback,
  );
  if (preferred !== fallback || input.selectedInstrument) return preferred;

  const symbol =
    typeof input.selectedSymbol === "string"
      ? input.selectedSymbol
      : input.selectedSymbol?.symbol;
  return normalizeInstrumentSymbol(symbol, fallback);
}

export function instrumentLabel(symbol: string | null | undefined): string {
  const normalized = normalizeInstrumentSymbol(symbol);
  const instrument = INSTRUMENT_BY_SYMBOL.get(normalized);
  return instrument ? `${instrument.symbol} - ${instrument.name}` : normalized;
}

export function estimateFallbackPointRange(
  severity: string,
  instrumentSymbol: string | null | undefined,
): number {
  const symbol = normalizeInstrumentSymbol(instrumentSymbol);
  const instrument = INSTRUMENT_BY_SYMBOL.get(symbol);
  if (!instrument) return 3;

  const fraction = SEVERITY_FRACTION[severity] ?? SEVERITY_FRACTION.low;
  const vixTwentyDailyPct = 20 / 16 / 100;
  const dailyPoints =
    instrument.fallbackPrice * vixTwentyDailyPct * Math.abs(instrument.beta);
  return roundPointValue(Math.max(0.0001, dailyPoints * fraction));
}

function option(
  symbol: string,
  name: string,
  beta: number,
  fallbackPrice: number,
): InstrumentOption {
  return { symbol, name, group: "", beta, fallbackPrice };
}

function roundPointValue(value: number): number {
  const magnitude = Math.abs(value);
  if (magnitude >= 1) return Number(value.toFixed(1));
  if (magnitude >= 0.01) return Number(value.toFixed(4));
  return Number(value.toFixed(6));
}
